import { Box, Button, FormHelperText, Select, styled, Typography, TextField } from "@mui/material";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { InputStyleType } from "../../../input/FormInput";

export const StyledContainer = styled(Box)(({ theme }) => ({
  textAlign: "center",
}));

export const StyledItem = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(2),
}));

export const StyledLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 500,
  marginBottom: "8px",
  letterSpacing: 0.2,
  background: "transparent",
  paddingLeft: 0,
  paddingRight: 0,
}));

export const StyledValue = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  paddingLeft: theme.spacing(1),
  fontSize: "14px",
}));

export const StyledIconContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  marginBottom: theme.spacing(2),
}));

export const StyledAmountContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  border: `1px solid #E5E7E8`,
  borderRadius: "4px",
  padding: "8px 16px",
}));

export const StyledIconWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "30px",
  height: "30px",
  borderRadius: "50%",
  overflow: "hidden",
  flexShrink: 0,
  padding: "0",
}));

export const MaxButton = styled(Button)(({ theme }) => ({
  padding: "4px 8px",
  height: "24px",
  minWidth: "27px",
  textTransform: "none",
  fontSize: "14px",
  fontWeight: 600,
  lineHeight: "16px",
  boxShadow: "none",
  marginLeft: "12px",
  borderRadius: "4px",
  background: "#002035",
  "&:hover": {
    background: "#4E7086",
    boxShadow: "none",
  },
  "&:active": {
    background: "#002035",
    boxShadow: "none",
  },
}));

// Hide number input spinners
export const StyledTextField = styled(TextField)({
  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
    "-webkit-appearance": "none",
    margin: 0,
  },
  "& input[type=number]": {
    "-moz-appearance": "textfield",
  },
});

// Add a new styled component for the balance text
export const StyledBalanceText = styled(Typography)(({ theme }) => ({
  padding: "4px 8px",
  fontSize: "14px",
  color: "#5C6469",
  whiteSpace: "nowrap",
  fontWeight: 400,
  overflow: "hidden",
  textOverflow: "ellipsis",
  background: "#F8F9F9",
  borderRadius: "8px",
}));

export const StyledSuccessIcon = styled(CheckCircleIcon)(({ theme }) => ({
  color: theme.palette.success.main,
  fontSize: 48,
}));

export const StyledSelect = styled(Select<string>)(({ theme }) => ({
  "& .MuiSelect-icon": {
    color: theme.palette.primary.main,
  },
  border: `1px solid #E5E7E8`,
  borderRadius: "8px",
  height: "48px",
  display: "flex",
  alignItems: "center",
  boxShadow: "none",
  background: theme.palette.background.paper,
  fontSize: "14px",
  marginTop: 0,
  padding: "0 12px",
  // Remove MUI's default border and shadow to match the figma design
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiOutlinedInput-root": {
    border: "none",
    boxShadow: "none",
  },
  "& .MuiInputBase-root": {
    border: "none",
    boxShadow: "none",
    background: "transparent",
  },
  // Remove the default padding from MuiSelect-select
  "& .MuiSelect-select": {
    padding: "8px",
  },
}));

export const StyledErrorBox = styled(Box)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: "14px",
  marginTop: theme.spacing(1),
  display: "flex",
  alignItems: "center",
  gap: 0.5,
}));

export const StyledInfoBox = styled(Box)(({ theme }) => ({
  background: "#FEFCF8",
  borderRadius: "12px",
  padding: "8px",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

export const StyledInfoHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(1),
}));

export const StyledInfoText = styled(Box)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: "14px",
  fontWeight: 400,
}));

export const StyledInfoTitle = styled(Box)(({ theme }) => ({
  paddingLeft: "5px",
  fontWeight: 700,
  fontSize: "14px",
  color: theme.palette.text.primary,
}));

export const StyledAmountInputContainer = styled(StyledAmountContainer)(({ theme }) => ({
  border: `1px solid #E5E7E8`,
  borderRadius: "8px",
  background: theme.palette.background.paper,
  padding: "0 12px",
  alignItems: "center",
  height: "48px",
  boxShadow: "none",
  "&.error": {
    border: `1px solid ${theme.palette.error.light}`,
  },
  "&.disabled": {
    opacity: 0.5,
    backgroundColor: "#F5F5F5",
    cursor: "not-allowed",
  },
}));

export const StyledAmountInput = styled(StyledTextField)(({ theme }) => ({
  "& .MuiInputBase-root": {
    fontSize: "14px",
    marginLeft: theme.spacing(2),
    background: "transparent",
    padding: 0,
    color: theme.palette.text.secondary,
  },
  "& input": {
    padding: 0,
    color: theme.palette.text.secondary,
  },
}));

// Section title for review screens
export const ReviewSectionTitle = styled(Typography)({
  color: "#5C6469",
  fontSize: "14px",
  fontWeight: 400,
});

// Copied from CryptoWithdrawalStyled.tsx
export const StyledExplainerTextTypography = styled(Typography)(() => ({
  fontSize: "14px",
  lineHeight: "17px",
  padding: "12px 0",
  color: "#5C6469",
}));

export const StyledFormContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  position: "relative",
}));

export const StyledFormHelperText = styled(FormHelperText)(({ theme }) => ({
  marginLeft: "0px",
  display: "ruby-text",
  alignItems: "center",
  gap: "8px",
  color: theme.palette.secondary.main,
}));

export const StyledSecurityNoticeTypography = styled(Typography)(({ theme }) => ({
  marginBottom: "28px",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "19px",
}));

export const StyledStepHeaderTypography = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 600,
  color: theme.palette.secondary.main,
}));

export const StyledMainContainer = styled(Box)(() => ({
  height: "100%",
  alignItems: "center",
  padding: "22px",
  flexGrow: 1,
  overflow: "auto",
}));

export const StyledModalTitle = styled(Typography)(() => ({
  fontSize: "23px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "28px ",
  letterSpacing: " -0.46px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  weight: "100%",
}));

export const StyledNextButton = styled(Button)(({ theme, disabled }) => ({
  backgroundColor: disabled
    ? theme.palette.action.disabledBackground
    : theme.palette.secondary.main,
  width: "100%",
  fontSize: "18px",
  height: "48px",
  textTransform: "capitalize",
  color: disabled ? theme.palette.action.disabled : "#fff",
  cursor: disabled ? "not-allowed" : "pointer",
  "&:hover": {
    backgroundColor: disabled ? theme.palette.action.disabledBackground : "#FEDBDB",
  },
}));

export const StyledWithdrawalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  width: "500px",
  height: "auto",
  backgroundColor: "#fff",
  borderRadius: "10px",
  transform: "translate(-50%, -50%)",
  boxShadow: "24",
  display: "block",
  flexDirection: undefined, // Only set flexDirection for non-mobile
  [theme.breakpoints.down("md")]: {
    borderRadius: "0px",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    transform: "none",
    boxShadow: "none",
    flexDirection: "column",
  },
}));

export const StyledTitleContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: "#fff",
  width: "100%",
  paddingRight: "28px",
  paddingLeft: "28px",
  borderRadius: "10px 10px 0px 0px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  height: "60px",
  [theme.breakpoints.down("md")]: {
    borderRadius: "0px",
  },
}));

export const StyledInputContainer = styled(TextField)<{
  styletype?: InputStyleType;
  inputSx?: any;
}>(({ theme, disabled, styletype, inputSx }) => ({
  height: "43px",
  position: "relative",
  minWidth: 200,
  ".MuiInputBase-root": {
    borderRadius: "8px",
    height: "100%",
  },
  "& .MuiOutlinedInput-input": {
    padding: "0px",
  },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    {
      WebkitAppearance: "none",
      margin: 0,
    },
  "& input[type=number]": {
    MozAppearance: "textfield",
  },
  ...(styletype === InputStyleType.ERROR && {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#EF4444",
      },
      "&:hover fieldset": {
        borderColor: "#EF4444",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#EF4444",
      },
    },
  }),
  ...(styletype === InputStyleType.FORM && {
    ".MuiInputBase-root": {
      fontSize: "14px",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
    },
  }),
  ...(styletype === InputStyleType.FORM_ERROR && {
    ".MuiInputBase-root": {
      fontSize: "14px",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      "& fieldset": {
        borderColor: "#EF4444",
      },
      "&:hover fieldset": {
        borderColor: "#EF4444",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#EF4444",
      },
    },
  }),
  ...inputSx,
}));

export const FiatWithdrawalVerificationError = styled("div")({
  fontFamily: "Söhne, sans-serif",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "14px",
  color: "#fc4c4c",
  marginTop: "4px",
});
